/**
 * GoogleCallback — handles redirect back from auth.da1timetable.ai / .info
 *
 * URL: /auth/google-callback?credential=<google-id-token>
 *
 * Da1 auth host: POST user/universal-login/google → school redirect (like email universal login).
 * School subdomain: POST user/login/google → tenant session.
 */
import {UniversalLoginInstance} from 'app/modules/auth/types/universalLogin'
import {getPublicLoginPath, isDa1AuthUniversalLoginHost} from 'common/HelperFunctions'
import {handlePostLoginNavigation} from 'common/helpers'
import {setUniversalLoginCanonicalDomain} from 'common/helpers/universalLoginHandoff'
import {
  applyUniversalLoginRedirect,
  isUniversalLoginConflict,
} from 'common/helpers/universalLoginResponse'
import {useEffect, useRef, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {Link, Navigate, useNavigate, useSearchParams} from 'react-router'
import {RootState} from 'setup'
import {loginWithGoogle, universalLoginWithGoogle} from 'setup/redux/Actions/SignIn/login'

import {toAbsoluteUrl} from '_metronic/helpers'

import {useAiLoginError} from '../hooks/useAiLoginError'
import {UniversalLoginSchoolPicker} from '../modals/UniversalLoginSchoolPicker'
import {AiLoginErrorAlert} from './AiLoginErrorAlert'

export function GoogleCallback() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const credential = searchParams.get('credential')
  const dispatch = useDispatch()
  const exchangeStarted = useRef(false)

  const [genericError, setGenericError] = useState('')
  const [loading, setLoading] = useState(true)
  const [showSchoolPicker, setShowSchoolPicker] = useState(false)
  const [schoolInstances, setSchoolInstances] = useState<UniversalLoginInstance[]>([])
  const [pendingGoogleToken, setPendingGoogleToken] = useState<string | null>(null)
  const [pickerLoading, setPickerLoading] = useState(false)

  const {aiLoginError, processAiLoginError, showEmailLoginForm} = useAiLoginError()

  const isLoggedIn = useSelector<RootState>(
    (state) => state.userReducer.isLoggedIn,
    shallowEqual
  ) as boolean

  const isDa1Marketing = isDa1AuthUniversalLoginHost()
  const loginPath = getPublicLoginPath()

  const redirectToEmailLogin = (email?: string) => {
    showEmailLoginForm(email)
    navigate(loginPath, {
      replace: true,
      state: {emailLoginOnly: true, email},
    })
  }

  const handleFailure = (res: any, idToken: string, fallbackMessage: string) => {
    if (isUniversalLoginConflict(res)) {
      setSchoolInstances(res.result.instances)
      setPendingGoogleToken(idToken)
      setShowSchoolPicker(true)
      setPickerLoading(false)
      setLoading(false)
      return
    }

    setGenericError('')
    processAiLoginError(res, (message) => setGenericError(message), fallbackMessage)
    setLoading(false)
  }

  const runGoogleExchange = (idToken: string, applicationId?: number) => {
    if (isDa1Marketing) {
      dispatch(
        universalLoginWithGoogle(
          idToken,
          (res: any) => {
            if (!applyUniversalLoginRedirect(res?.result)) {
              setGenericError('Google sign-in succeeded but no redirect URL was returned.')
              setLoading(false)
            }
          },
          (res: any) => handleFailure(res, idToken, 'Google sign-in failed'),
          applicationId
        )
      )
      return
    }

    dispatch(
      loginWithGoogle(
        idToken,
        (res: any) => {
          if (
            handlePostLoginNavigation(res.result, {
              path: '/dashboard',
              searchParams: {step: '0'},
            })
          ) {
            return
          }
          setLoading(false)
        },
        (err: any) => handleFailure(err, idToken, 'Google login failed')
      )
    )
  }

  useEffect(() => {
    if (!credential) {
      setGenericError('No Google credential received')
      setLoading(false)
      return
    }
    if (exchangeStarted.current) return
    exchangeStarted.current = true
    runGoogleExchange(credential)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credential])

  const handleSchoolSelect = (instance: UniversalLoginInstance) => {
    if (!pendingGoogleToken) return
    if (instance.canonicalDomain) {
      setUniversalLoginCanonicalDomain(instance.canonicalDomain)
    }
    setPickerLoading(true)
    runGoogleExchange(pendingGoogleToken, instance.applicationId)
  }

  if (isLoggedIn) {
    return <Navigate to='/dashboard?step=0' replace />
  }

  const pageBg = {
    background: 'linear-gradient(90.14deg, #431214 0.12%, #031C36 99.87%)',
  }

  if (showSchoolPicker) {
    return (
      <div className='d-flex flex-column flex-column-fluid min-vh-100' style={pageBg}>
        <UniversalLoginSchoolPicker
          show={showSchoolPicker}
          instances={schoolInstances}
          loading={pickerLoading}
          onSelect={handleSchoolSelect}
          onClose={() => {
            if (pickerLoading) return
            setShowSchoolPicker(false)
            setSchoolInstances([])
            setPendingGoogleToken(null)
          }}
        />
      </div>
    )
  }

  if (aiLoginError || genericError) {
    return (
      <div className='d-flex flex-column flex-column-fluid min-vh-100' style={pageBg}>
        <div className='position-absolute top-0 start-0 p-10 d-flex align-items-center gap-2'>
          <img
            src={toAbsoluteUrl('/media/auth/image 70.png')}
            alt='Logo'
            style={{maxHeight: '48px'}}
          />
          <span className='text-white fw-bold fs-4'>Timetable™</span>
        </div>

        <div className='d-flex flex-center flex-column flex-grow-1 px-4'>
          <div
            className='text-center px-8 py-10'
            style={{
              background: 'rgba(40, 40, 50, 0.85)',
              backdropFilter: 'blur(12px)',
              borderRadius: 24,
              maxWidth: 420,
              width: '100%',
              boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
            }}
          >
            {aiLoginError ? (
              <AiLoginErrorAlert error={aiLoginError} onSignInWithEmail={redirectToEmailLogin} />
            ) : (
              <>
                <div className='d-flex justify-content-center mb-5'>
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'rgba(239, 68, 68, 0.15)',
                    }}
                  >
                    <i className='fa fa-exclamation-triangle text-danger' style={{fontSize: 32}} />
                  </div>
                </div>
                <h3 className='text-white fw-bolder mb-3'>Login Failed</h3>
                <p className='text-white-50 fs-6 mb-8'>{genericError}</p>
              </>
            )}

            <Link
              to={loginPath}
              className='btn fw-bold w-100'
              style={{
                background: '#3b82f6',
                color: '#fff',
                border: 'none',
                borderRadius: 10,
                padding: '12px 28px',
                fontSize: 15,
                marginTop: aiLoginError ? 16 : 0,
              }}
            >
              Back to Login
            </Link>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div
        className='d-flex flex-column justify-content-center align-items-center vh-100'
        style={pageBg}
      >
        <img
          src={toAbsoluteUrl('/media/logos/LogoAnim1.gif')}
          alt='Loading'
          style={{width: 120, height: 120, objectFit: 'contain'}}
        />
        <h3 className='text-white fw-bolder mt-4 mb-2'>Signing you in…</h3>
        <p className='text-white-50 fs-5 mb-0'>
          Please wait while we securely log you into your account.
        </p>
      </div>
    )
  }

  return <Navigate to={loginPath} replace />
}
